import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { logger } from '../utils/logger';

const PdfContext = createContext();

export const PdfProvider = ({ children }) => {
  const REACT_APP_TEST_API_BASE_URL = process.env.REACT_APP_TEST_API_BASE_URL;
  const [pdfList, setPdfList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Chuyển fetchPdfs thành useCallback để tránh re-render không cần thiết
  const fetchPdfs = useCallback(async (retries = 3) => {
    try {
      const response = await axios.get(`${REACT_APP_TEST_API_BASE_URL}/api/pdfs`);
      return response.data;
    } catch (error) {
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchPdfs(retries - 1);
      }
      throw error;
    }
  }, []);

  // Sử dụng prefetchPdfs khi có dữ liệu PDF mới
  const prefetchPdfs = useCallback((pdfs) => {
    pdfs.forEach(pdf => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = pdf.pdfUrl;
      document.head.appendChild(link);
    });
  }, []);

  // Initial load
  useEffect(() => {
    const loadPdfs = async () => {
      const cachedPdfs = localStorage.getItem('pdfList');
      if (cachedPdfs) {
        const parsedPdfs = JSON.parse(cachedPdfs);
        setPdfList(parsedPdfs);
        prefetchPdfs(parsedPdfs);
        setIsLoading(false);
      }

      try {
        const newData = await fetchPdfs();
        setPdfList(newData);
        localStorage.setItem('pdfList', JSON.stringify(newData));
        prefetchPdfs(newData);
        setIsLoading(false);
      } catch (error) {
        logger.error("Lỗi khi tải danh sách PDF:", error);
        setIsLoading(false);
      }
    };

    loadPdfs();
  }, [fetchPdfs, prefetchPdfs]);

  // Refresh interval
  useEffect(() => {
    const refreshInterval = setInterval(async () => {
      try {
        const data = await fetchPdfs();
        setPdfList(data);
        localStorage.setItem('pdfList', JSON.stringify(data));
        prefetchPdfs(data);
      } catch (error) {
        logger.error("Lỗi khi refresh PDF:", error);
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [fetchPdfs, prefetchPdfs]);

  return (
    <PdfContext.Provider value={{ pdfList, isLoading }}>
      {children}
    </PdfContext.Provider>
  );
};

export const usePdf = () => useContext(PdfContext); 
